import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  branchesApi,
  branchExclusionsApi,
  type CreateBranchPayload,
  type UpdateBranchPayload,
  type BranchExclusionEntity,
} from '../api/branchesApi';
import { useToast } from '../../../shared/components/toast/useToast';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const KEY = 'branches';

/**
 * Branches for the selected merchant (PE-2139).
 *
 * Unlike most Osprey lists, scoping is an explicit `merchantId` param rather than the
 * x-tenant-id header — see the note in branchesApi.ts. The merchant id is therefore
 * both a query-key member and the enable-gate.
 */
export const useBranches = (merchantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, merchantId],
    queryFn: () => branchesApi.list(merchantId as string),
    enabled: !!merchantId,
    staleTime: 60 * 1000,
  });

export const useCreateBranch = (merchantId: string | null | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateBranchPayload) =>
      // COLLECTIVE-scoped callers must supply merchantId; merchant-scoped callers have it
      // forced server-side, so sending it is safe in both cases.
      branchesApi.create({ ...data, merchantId: data.merchantId ?? (merchantId as string) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, merchantId] });
      toast.success('Branch created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create branch')),
  });
};

export const useUpdateBranch = (merchantId: string | null | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBranchPayload }) => branchesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, merchantId] });
      toast.success('Branch updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update branch')),
  });
};

/**
 * "Remove" a branch. There is no DELETE route — the backend deactivates, which keeps
 * historical references intact, so the UI must offer re-activation rather than treating
 * this as destructive.
 */
export const useSetBranchActive = (merchantId: string | null | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      isActive ? branchesApi.activate(id) : branchesApi.deactivate(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [KEY, merchantId] });
      toast.success(variables.isActive ? 'Branch re-activated' : 'Branch removed');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update branch status')),
  });
};

const EXCLUSIONS_KEY = 'branch-exclusions';

/**
 * Branch ids at which the given entity is NOT available (PE-2139).
 * Remember the inversion: an entity defaults to available everywhere, so an empty
 * list means "available at all branches".
 */
export const useBranchExclusions = (entity: BranchExclusionEntity, entityId: string | null | undefined) =>
  useQuery({
    queryKey: [EXCLUSIONS_KEY, entity, entityId],
    queryFn: () => branchExclusionsApi.list(entity, entityId as string),
    enabled: !!entityId,
    staleTime: 60 * 1000,
  });

/**
 * Toggle availability of an entity at one branch. `available: false` writes an
 * exclusion; `available: true` removes it.
 */
export const useSetBranchAvailability = (entity: BranchExclusionEntity, entityId: string | null | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ branchId, available }: { branchId: string; available: boolean }) =>
      available
        ? branchExclusionsApi.include(entity, entityId as string, branchId)
        : branchExclusionsApi.exclude(entity, entityId as string, branchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EXCLUSIONS_KEY, entity, entityId] });
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update branch availability')),
  });
};
